import { useEffect, useMemo, useState } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { logspot as Logspot } from '@logspot/web';
import { toast } from 'sonner';
import { DownloadSimpleIcon, PlusIcon, PulseIcon } from '@phosphor-icons/react';
import {
  ACQUISITION_CHANNELS,
  SIGNAL_SOURCES,
  SIGNAL_TYPES,
  acquisitionChannelLabel,
  type CompanyProfile,
  type Signal,
} from '@logspot-platform/platform-types';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SignalRulesPanel } from '@/components/signals/signal-rules-panel';
import { PageContent } from '@/components/page-content';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { hasPermission } from '@/lib/permissions';
import { useFeatures } from '@/hooks/use-features';
import {
  companyProfilesOptions,
  signalsFeedOptions,
} from '@/hooks/query-options';
import { SignalRow, signalAccountName } from '@/components/signals/signal-row';
import {
  signalEvidence,
  signalSourceLabel,
  signalSourceForType,
  signalTypeLabel,
} from '@/components/signals/signal-labels';
import { downloadCsv } from '@/lib/csv';

type SignalsSearch = {
  type?: string;
  source?: string;
  acquisition_channel?: string;
  profile_id?: string;
};

// Actions › Signals (PRD 2.8): the org-wide GTM signals feed — newest first,
// filterable by type / source / account, cursor-paginated. The per-account
// timeline (PRD 2.9) is this same feed scoped by `profile_id`; the "View All"
// the insights link deep-links here with that filter applied.
export const Route = createFileRoute('/_auth/_side-layout/actions/signals')({
  component: SignalsPage,
  beforeLoad: async ({ context }) => {
    if (!hasPermission(context.userRole, { reports: ['read'] })) {
      throw redirect({ to: '/' });
    }
  },
  validateSearch: (search: Record<string, unknown>): SignalsSearch => ({
    type: typeof search.type === 'string' ? search.type : undefined,
    source:
      search.source === 'internal' || search.source === 'external'
        ? search.source
        : undefined,
    acquisition_channel:
      typeof search.acquisition_channel === 'string' &&
      (ACQUISITION_CHANNELS as readonly string[]).includes(
        search.acquisition_channel,
      )
        ? search.acquisition_channel
        : undefined,
    profile_id:
      typeof search.profile_id === 'string' ? search.profile_id : undefined,
  }),
});

// Explains the loop for orgs with the flag but no signals yet (PRD 5.1 / 6.1):
// what emits signals, and that Account Watch is the external source.
function SignalsEmptyState() {
  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <PulseIcon />
        </EmptyMedia>
        <EmptyTitle>No Signals Yet</EmptyTitle>
        <EmptyDescription>
          Signals appear when a Target Account shows intent — visiting your
          pricing or docs, returning within a week, or spiking in usage. Turn on
          Account Watch to add external signals too: funding, news, leadership
          changes, and hiring. Save a Target Account to start the loop.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

// Flag-off placeholder, matching the Target Accounts tab's "coming soon" state
// (super-admins enable the org's `gtmSignals` flag to see the real feed).
function ComingSoonState() {
  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <PulseIcon />
        </EmptyMedia>
        <EmptyTitle>Signals: Coming Soon</EmptyTitle>
        <EmptyDescription>
          A live feed of intent on the accounts you sell into: pricing visits,
          return traffic, usage spikes, and research findings like funding and
          leadership changes. Each one carries its evidence and can trigger an
          action.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

const payloadString = (payload: Record<string, unknown>, key: string) =>
  typeof payload[key] === 'string' ? (payload[key] as string) : null;

// One row per signal, columns named after the Clay webhook row (signal.*,
// account.*, evidence) so a CSV import and a webhook table line up.
function exportSignalsCsv(
  signals: Signal[],
  accountById: Map<string, CompanyProfile>,
) {
  const accountName = (profileId: string) => accountById.get(profileId)?.label;
  downloadCsv(
    'logspot-signals',
    [
      'signal_id',
      'signal_type',
      'signal_label',
      'signal_source',
      'signal_strength',
      'occurred_at',
      'account_name',
      'account_domain',
      'company_id',
      'profile_id',
      'evidence',
      'evidence_url',
      'acquisition_channel',
      'logspot_url',
    ],
    signals.map((signal) => {
      const evidence = signalEvidence(signal.payload);
      const account = signal.profile_id
        ? accountById.get(signal.profile_id)
        : undefined;
      return [
        signal.id,
        signal.type,
        signalTypeLabel(signal.type),
        signal.source,
        signal.strength,
        signal.occurred_at,
        signalAccountName(signal, accountName),
        account?.domain ?? payloadString(signal.payload, 'domain'),
        signal.company_id,
        signal.profile_id,
        evidence.text,
        evidence.url,
        payloadString(signal.payload, 'acquisition_channel'),
        signal.profile_id
          ? `${window.location.origin}/actions/profiles/${signal.profile_id}`
          : null,
      ];
    }),
  );
}

function SignalsPage() {
  const { isEnabled } = useFeatures();
  const enabled = isEnabled('gtmSignals');
  const rulesEnabled = isEnabled('configurableSignals');
  const { userRole } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [tab, setTab] = useState('feed');
  const [addOpen, setAddOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const canEditRules = userRole === 'owner' || userRole === 'admin';

  // Product-event instrumentation (PRD 6.4 activation funnel). Synchronizes a
  // page-view with the Logspot analytics SDK — the same external-system pattern
  // as the root route's "App Opened" track. Fires once per feed mount.
  useEffect(() => {
    Logspot.track({ event: 'Signals Viewed' });
  }, []);

  const { data: profilesData } = useQuery({
    ...companyProfilesOptions(),
    // No fetch behind the coming-soon wall.
    enabled,
  });
  const accounts = useMemo(
    () => (profilesData?.profiles ?? []).filter((p) => p.kind === 'account'),
    [profilesData],
  );
  const accountById = useMemo(
    () => new Map(accounts.map((a) => [a.id, a] as const)),
    [accounts],
  );
  // Resolve profile_id → account label for the rows (and the account filter).
  const accountName = useMemo(
    () => (profileId: string) => accountById.get(profileId)?.label,
    [accountById],
  );

  const feed = useInfiniteQuery({
    ...signalsFeedOptions({
      type: search.type,
      source: search.source,
      acquisitionChannel: search.acquisition_channel,
      profileId: search.profile_id,
    }),
    enabled,
  });
  const signals = useMemo(
    () => (feed.data?.pages ?? []).flatMap((page) => page.signals),
    [feed.data],
  );

  // Exports every signal matching the current filters, not just the loaded
  // pages, so it walks the remaining pages first.
  const handleExport = async () => {
    setIsExporting(true);
    try {
      let pages = feed.data?.pages ?? [];
      if (feed.hasNextPage) {
        let page = await feed.fetchNextPage({ throwOnError: true });
        while (page.hasNextPage) {
          page = await feed.fetchNextPage({ throwOnError: true });
        }
        pages = page.data?.pages ?? pages;
      }
      const all = pages.flatMap((page) => page.signals);
      exportSignalsCsv(all, accountById);
      Logspot.track({
        event: 'Signals Exported',
        metadata: { rows: all.length },
      });
    } catch {
      toast.error('Could not export signals. Try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const setFilter = (patch: Partial<SignalsSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }) });

  const openAccount = (profileId: string) =>
    navigate({
      to: '/actions/profiles/$profileId',
      params: { profileId },
    });

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <PageHeader
        title="Signals"
        isNew
        description="Intent and research findings on the accounts you sell into, newest first."
        actions={
          enabled && rulesEnabled && canEditRules ? (
            <Button
              iconLeft={<PlusIcon />}
              onClick={() => {
                setTab('rules');
                setAddOpen(true);
              }}
            >
              Add Signal
            </Button>
          ) : undefined
        }
      >
        {enabled && rulesEnabled && (
          <TabsList>
            <TabsTrigger value="feed">Feed</TabsTrigger>
            <TabsTrigger value="rules">Rules</TabsTrigger>
          </TabsList>
        )}
      </PageHeader>
      <PageContent>
        {!enabled ? (
          <ComingSoonState />
        ) : (
          <>
            {rulesEnabled && (
              <TabsContent value="rules">
                <SignalRulesPanel
                  canEdit={canEditRules}
                  addOpen={addOpen}
                  onAddOpenChange={setAddOpen}
                  onViewSignal={(type) => {
                    setFilter({ type });
                    setTab('feed');
                  }}
                  onConfigureWatch={() =>
                    navigate({
                      to: '/actions/your-actions/actions',
                      search: { editAction: 'account.watch' },
                    })
                  }
                  canConfigureWatch={
                    canEditRules && isEnabled('targetAccounts')
                  }
                />
              </TabsContent>
            )}
            <TabsContent value="feed">
              <div className="space-y-4">
                {/* Filters: type (friendly labels), source, account. `all` is the
                cleared value for each — mapped back to an undefined filter. */}
                <div className="flex flex-wrap items-center gap-2.5">
                  <Select
                    value={search.type ?? 'all'}
                    onValueChange={(value) =>
                      setFilter({ type: value === 'all' ? undefined : value })
                    }
                  >
                    <SelectTrigger className="w-56">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {SIGNAL_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {signalSourceLabel(signalSourceForType(type))}:{' '}
                          {signalTypeLabel(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={search.source ?? 'all'}
                    onValueChange={(value) =>
                      setFilter({ source: value === 'all' ? undefined : value })
                    }
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      {SIGNAL_SOURCES.map((source) => (
                        <SelectItem key={source} value={source}>
                          {signalSourceLabel(source)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={search.acquisition_channel ?? 'all'}
                    onValueChange={(value) =>
                      setFilter({
                        acquisition_channel:
                          value === 'all' ? undefined : value,
                      })
                    }
                  >
                    <SelectTrigger className="w-44">
                      <SelectValue placeholder="All Channels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Channels</SelectItem>
                      {ACQUISITION_CHANNELS.map((channel) => (
                        <SelectItem key={channel} value={channel}>
                          {acquisitionChannelLabel(channel)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {accounts.length > 0 && (
                    <Select
                      value={search.profile_id ?? 'all'}
                      onValueChange={(value) =>
                        setFilter({
                          profile_id: value === 'all' ? undefined : value,
                        })
                      }
                    >
                      <SelectTrigger className="w-56">
                        <SelectValue placeholder="All Accounts" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Accounts</SelectItem>
                        {accounts.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <div className="flex-1" />
                  <Button
                    variant="outline"
                    iconLeft={<DownloadSimpleIcon />}
                    onClick={handleExport}
                    loading={isExporting}
                    disabled={
                      (signals.length === 0 && !feed.hasNextPage) || isExporting
                    }
                  >
                    Export CSV
                  </Button>
                </div>

                {feed.isLoading ? (
                  <p className="text-muted-foreground text-sm">Loading…</p>
                ) : signals.length === 0 ? (
                  <SignalsEmptyState />
                ) : (
                  <>
                    <div className="space-y-2.5">
                      {signals.map((signal) => (
                        <SignalRow
                          key={signal.id}
                          signal={signal}
                          accountName={accountName}
                          onOpen={openAccount}
                        />
                      ))}
                    </div>
                    {feed.hasNextPage && (
                      <div className="flex justify-center pt-2">
                        <Button
                          variant="outline"
                          loading={feed.isFetchingNextPage}
                          onClick={() => feed.fetchNextPage()}
                        >
                          Load More
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </TabsContent>
          </>
        )}
      </PageContent>
    </Tabs>
  );
}
